import React, { ReactNode } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Row from "antd/es/row";
import { useIntl } from "react-intl";
import invariant from "tiny-invariant";

import { FrequencyStatus } from "@mapmycustomers/shared";
import Cadenced from "@mapmycustomers/shared/types/base/Cadenced";
import { Activity } from "@mapmycustomers/shared/types/entity";

import CollapseCard from "@app/component/CollapseCard";
import FrequencyIcon from "@app/component/frequency/FrequencyIcon";
import FrequencyPreview from "@app/component/frequency/FrequencyPreview";
import FrequencyEntity from "@app/component/frequency/type/FrequencyEntity";
import { isFrequencyModalLoading } from "@app/store/frequency";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import useToggleBooleanAfterTimeout from "@app/util/hook/useToggleBooleanAfterTimeout";

import styles from "./FrequencyBlock.module.scss";

interface Props {
  cadenceInterval?: Cadenced["cadenceInterval"];
  cadenceStatus?: FrequencyStatus;
  crmActivityTypeIds?: Cadenced["crmActivityTypeId"];
  entity?: FrequencyEntity;
  header?: ReactNode;
  isGroup?: boolean;
  isOpenByDefault?: boolean;
  lastCompletedActivity?: Activity;
  loading: boolean;
  readonly?: boolean;
}

const FrequencyBlock = ({
  cadenceInterval,
  cadenceStatus,
  crmActivityTypeIds,
  entity,
  header,
  isGroup,
  isOpenByDefault,
  lastCompletedActivity,
  loading,
  readonly,
}: Props) => {
  const intl = useIntl();
  const showLoading = useToggleBooleanAfterTimeout(1000, false, loading, true);

  invariant(
    entity || (cadenceInterval && cadenceStatus),
    "Either entity or cadenceInterval and cadenceStatus must be provided"
  );

  return (
    <div className={styles.container}>
      <CollapseCard
        className={styles.collapse}
        header={
          <Row align="middle" gutter={layout.spacerS}>
            <Col>
              <FrequencyIcon
                size="tiny"
                status={cadenceStatus ?? entity?.cadenceStatus ?? FrequencyStatus.UNKNOWN}
              />
            </Col>
            <Col>
              {intl.formatMessage(
                {
                  id: "frequency.frequencyModal.frequencies.groups.title",
                  defaultMessage: "{count} Day Frequency",
                  description: "frequency component - frequency modal - frequencies - title",
                },
                { count: cadenceInterval ?? entity?.cadenceInterval ?? 0 }
              )}
            </Col>
          </Row>
        }
        isOpenByDefault={isOpenByDefault}
      >
        <FrequencyPreview
          cadenceInterval={cadenceInterval ?? entity?.cadenceInterval ?? 0}
          crmActivityTypeIds={crmActivityTypeIds}
          entity={entity}
          header={header}
          isGroup={isGroup}
          lastCompletedActivity={lastCompletedActivity}
          loading={showLoading}
          readonly={readonly}
          saveButtonText={intl.formatMessage({
            id: "frequency.frequencyModal.frequencies.saveText",
            defaultMessage: "Save Changes",
            description: "frequency component - frequency modal - frequencies - save Text",
          })}
          showSaveButtonsOnlyAfterChanges
        />
      </CollapseCard>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  loading: isFrequencyModalLoading(state),
});

export default connect(mapStateToProps)(FrequencyBlock);
